import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Container,
  Grid,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Database, GitCompareArrows, Upload, Users } from 'lucide-react';
import PageHeader from '@components/shared/PageHeader';
import {
  migrateLegacyUserFromJson,
  verifyLegacyUserFromJson,
  type LegacyMigrationVerification,
  type LegacyUserMigrationExportPayload,
} from '../services/legacyUserManager';
import {
  checkV0EmailsInBe2025,
  migrateLegacyV0UserFromJson,
  type LegacyV0UserMigrationExportPayload,
} from '../services/legacyV0UserManager';
import { LegacyMigrationVerificationResult } from './components/LegacyMigrationVerificationResult';

type HubMigrationPayload =
  | (LegacyUserMigrationExportPayload & { legacy_source?: 'v1' })
  | (LegacyV0UserMigrationExportPayload & { legacy_source?: 'v0' });

type LegacySource = 'v0' | 'v1';

const SHARED_EXPORT_KIND = 'legacy_user_migration_export';
const LEGACY_V0_EXPORT_KIND = 'legacy_v0_user_migration_export';

const resolveLegacySource = (parsed: Record<string, unknown>): LegacySource | null => {
  const kind = parsed.kind;
  if (kind === LEGACY_V0_EXPORT_KIND) return 'v0';
  if (kind !== SHARED_EXPORT_KIND) return null;
  if (parsed.legacy_source === 'v0') return 'v0';
  if (parsed.legacy_source === 'v1') return 'v1';
  const source = parsed.source;
  if (source && typeof source === 'object' && !Array.isArray(source)) {
    const hint = (source as { schema_hint?: string }).schema_hint;
    if (hint === 'legacy_v0_migration_source') return 'v0';
  }
  // Shared kind without explicit source → v1 (historical exports)
  return 'v1';
};

type HubCard = {
  title: string;
  description: string;
  to?: string;
  icon: typeof Users;
  badge: string;
  disabled?: boolean;
};

const V1_CARDS: HubCard[] = [
  {
    title: 'Legacy users',
    description:
      'Inventory vapor users, preview remaps, migrate courses/videos into be2025. Writes Users.legacy_source = v1.',
    to: '/admin/legacy-users',
    icon: Users,
    badge: 'migrate',
  },
  {
    title: 'Catalog mapping',
    description:
      'How vapor courses, videos and questions resolve to be2025 catalog IDs (name-based remap).',
    to: '/admin/legacy-users/mapping',
    icon: GitCompareArrows,
    badge: 'remap',
  },
  {
    title: 'DB schema',
    description:
      'Browse vapor tables and sample rows (staging RDS, read-only).',
    to: '/admin/legacy-users/schema',
    icon: Database,
    badge: 'schema',
  },
];

const V0_CARDS: HubCard[] = [
  {
    title: 'Legacy users',
    description:
      'Inventory WordPress clienti, export JSON/Excel Contatti, migrate apps → UserProducts. Writes Users.legacy_source = v0.',
    to: '/admin/legacy-v0-users',
    icon: Users,
    badge: 'migrate',
  },
  {
    title: 'Catalog mapping',
    description: 'v0 → be2025 catalog remap. Pending.',
    icon: GitCompareArrows,
    badge: 'soon',
    disabled: true,
  },
  {
    title: 'DB schema',
    description: 'Browse v0 source schema. Pending.',
    icon: Database,
    badge: 'soon',
    disabled: true,
  },
];

function MigrationSection({
  source,
  title,
  subtitle,
  badgeColor,
  cards,
}: {
  source: string;
  title: string;
  subtitle: string;
  badgeColor: string;
  cards: HubCard[];
}) {
  const navigate = useNavigate();

  return (
    <Stack gap='md'>
      <Group justify='space-between' align='flex-start' wrap='wrap'>
        <div>
          <Group gap='sm' mb={4}>
            <Title order={3}>{title}</Title>
            <Badge color={badgeColor} variant='filled'>
              {source}
            </Badge>
          </Group>
          <Text size='sm' c='dimmed'>
            {subtitle}
          </Text>
        </div>
      </Group>

      <Grid>
        {cards.map((card) => {
          const disabled = Boolean(card.disabled || !card.to);
          return (
            <Grid.Col key={`${source}-${card.title}`} span={{ base: 12, sm: 6, md: 4 }}>
              <Card
                withBorder
                padding='lg'
                radius='md'
                style={{
                  cursor: disabled ? 'default' : 'pointer',
                  height: '100%',
                  opacity: disabled ? 0.55 : 1,
                }}
                onClick={() => {
                  if (!disabled && card.to) navigate(card.to);
                }}
              >
                <Stack gap='md' h='100%'>
                  <Group justify='space-between'>
                    <card.icon
                      size={28}
                      color={
                        disabled
                          ? 'var(--mantine-color-gray-5)'
                          : `var(--mantine-color-${badgeColor}-6)`
                      }
                    />
                    <Badge
                      variant='light'
                      color={disabled ? 'gray' : badgeColor}
                    >
                      {card.badge}
                    </Badge>
                  </Group>
                  <Title order={4}>{card.title}</Title>
                  <Text size='sm' c='dimmed'>
                    {card.description}
                  </Text>
                </Stack>
              </Card>
            </Grid.Col>
          );
        })}
      </Grid>
    </Stack>
  );
}

type QueueItem = {
  filename: string;
  payload: HubMigrationPayload;
  legacySource: LegacySource;
};

type EmailPresence = 'loading' | 'present' | 'absent' | 'unknown';

type QueueStats = {
  migrated: number;
  skipped: number;
  failed: number;
};

const emptyStats = (): QueueStats => ({
  migrated: 0,
  skipped: 0,
  failed: 0,
});

function parseMigrationFile(
  file: File
): Promise<{ ok: true; item: QueueItem } | { ok: false; reason: string }> {
  return (async () => {
    if (file.type && !file.type.includes('json') && !file.name.endsWith('.json')) {
      return { ok: false as const, reason: `${file.name}: not a .json file` };
    }
    try {
      const text = await file.text();
      const parsed = JSON.parse(text) as Record<string, unknown>;
      const source = resolveLegacySource(parsed);
      if (!source) {
        return {
          ok: false as const,
          reason: `${file.name}: unsupported kind "${String(parsed.kind)}"`,
        };
      }
      if (!parsed.legacy_user_id || !parsed.email) {
        return {
          ok: false as const,
          reason: `${file.name}: missing legacy_user_id or email`,
        };
      }
      return {
        ok: true as const,
        item: {
          filename: file.name,
          payload: parsed as HubMigrationPayload,
          legacySource: source,
        },
      };
    } catch (err) {
      return {
        ok: false as const,
        reason: `${file.name}: ${(err as Error).message || 'invalid JSON'}`,
      };
    }
  })();
}

function MigrateFromJsonPanel() {
  const inputRef = useRef<HTMLInputElement>(null);
  const knownPresentEmailsRef = useRef<Set<string>>(new Set());
  const [dragging, setDragging] = useState(false);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [index, setIndex] = useState(0);
  const [stats, setStats] = useState<QueueStats>(emptyStats);
  const [modalOpen, setModalOpen] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [verification, setVerification] =
    useState<LegacyMigrationVerification | null>(null);
  const [emailPresence, setEmailPresence] = useState<EmailPresence>('loading');

  const current = queue[index] ?? null;
  const payload = current?.payload ?? null;
  const legacySource = current?.legacySource ?? null;
  const filename = current?.filename ?? null;

  const reset = useCallback(() => {
    setModalOpen(false);
    setQueue([]);
    setIndex(0);
    setStats(emptyStats());
    setVerification(null);
    setVerifying(false);
    setMigrating(false);
    setEmailPresence('loading');
    knownPresentEmailsRef.current = new Set();
  }, []);

  const finishQueue = useCallback(
    (finalStats: QueueStats) => {
      notifications.show({
        color: 'blue',
        message: `Queue done · migrated ${finalStats.migrated} · skipped ${finalStats.skipped} · failed ${finalStats.failed}`,
      });
      reset();
    },
    [reset]
  );

  const advance = useCallback(
    (nextStats: QueueStats) => {
      setVerification(null);
      setVerifying(false);
      setMigrating(false);
      setStats(nextStats);
      if (index + 1 >= queue.length) {
        finishQueue(nextStats);
        return;
      }
      setIndex((prev) => prev + 1);
    },
    [finishQueue, index, queue.length]
  );

  const acceptFiles = useCallback(async (fileList: FileList | File[] | null) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    const parsed = await Promise.all(files.map((file) => parseMigrationFile(file)));
    const items: QueueItem[] = [];
    const rejected: string[] = [];
    for (const result of parsed) {
      if (result.ok) items.push(result.item);
      else rejected.push(result.reason);
    }
    if (rejected.length > 0) {
      const preview = rejected.slice(0, 5).join('; ');
      const more =
        rejected.length > 5 ? ` (+${rejected.length - 5} more)` : '';
      notifications.show({
        color: 'red',
        message: `${rejected.length} file(s) skipped: ${preview}${more}`,
      });
    }
    if (items.length === 0) {
      notifications.show({
        color: 'red',
        message: 'No valid migration JSON files in selection',
      });
      return;
    }
    setQueue(items);
    setIndex(0);
    setStats(emptyStats());
    setVerification(null);
    knownPresentEmailsRef.current = new Set();
    setModalOpen(true);
  }, []);

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragging(false);
    void acceptFiles(event.dataTransfer.files);
  };

  useEffect(() => {
    if (!modalOpen || !payload?.email) {
      setEmailPresence('loading');
      return;
    }
    const email = String(payload.email).trim().toLowerCase();
    if (!email) {
      setEmailPresence('unknown');
      return;
    }
    if (knownPresentEmailsRef.current.has(email)) {
      setEmailPresence('present');
      return;
    }
    let cancelled = false;
    setEmailPresence('loading');
    void (async () => {
      try {
        const present = await checkV0EmailsInBe2025([email]);
        if (cancelled) return;
        if (present.has(email)) {
          knownPresentEmailsRef.current.add(email);
          setEmailPresence('present');
        } else {
          setEmailPresence('absent');
        }
      } catch {
        if (!cancelled) setEmailPresence('unknown');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modalOpen, payload?.email, index]);

  const handleSkip = () => {
    if (!current || migrating || verifying) return;
    const nextStats = { ...stats, skipped: stats.skipped + 1 };
    advance(nextStats);
  };

  const handleVerify = async () => {
    if (!payload || !legacySource) return;
    if (legacySource === 'v0') {
      notifications.show({
        color: 'blue',
        message: 'Verify from JSON is available for v1 exports only',
      });
      return;
    }
    setVerifying(true);
    setVerification(null);
    try {
      const result = await verifyLegacyUserFromJson(
        payload as LegacyUserMigrationExportPayload
      );
      setVerification(result);
      notifications.show({
        color:
          result.status === 'pass'
            ? 'teal'
            : result.status === 'not_linked'
              ? 'blue'
              : 'red',
        message:
          result.status === 'pass'
            ? `Verify PASS · json→be · #${payload.legacy_user_id}`
            : result.status === 'not_linked'
              ? `Verify: not linked yet — migrate first (#${payload.legacy_user_id})`
              : `Verify FAIL · ${result.summary.mismatches} mismatch(es)`,
      });
    } catch (err) {
      notifications.show({
        color: 'red',
        message: (err as Error).message || 'Verify from JSON failed',
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleMigrate = async () => {
    if (!payload || !legacySource) return;
    if (
      !window.confirm(
        `Migrate ${legacySource.toUpperCase()} JSON for legacy #${payload.legacy_user_id} (${payload.email}) on this environment?\nUses JSON only — fill-missing (existing be2025 data wins).`
      )
    ) {
      return;
    }
    setMigrating(true);
    try {
      if (legacySource === 'v0') {
        // Staging may still require legacy kind until services redeploy accepts shared kind.
        const v0Payload: LegacyV0UserMigrationExportPayload = {
          ...(payload as LegacyV0UserMigrationExportPayload),
          kind:
            payload.kind === SHARED_EXPORT_KIND
              ? LEGACY_V0_EXPORT_KIND
              : (payload.kind as LegacyV0UserMigrationExportPayload['kind']),
        };
        const result = await migrateLegacyV0UserFromJson(v0Payload, {
          dryRun: false,
        });
        if (!result.dryRun && result.applied) {
          notifications.show({
            color: 'green',
            message: `Migrated v0 JSON → be2025 user ${result.applied.new_user_id} (legacy #${payload.legacy_user_id})`,
          });
        }
      } else {
        const result = await migrateLegacyUserFromJson(
          payload as LegacyUserMigrationExportPayload,
          { dryRun: false }
        );
        if (!result.dryRun && 'applied' in result && result.applied) {
          notifications.show({
            color: 'green',
            message: `Migrated v1 JSON → be2025 user ${result.applied.new_user_id} (legacy #${payload.legacy_user_id})`,
          });
        }
      }
      const email = String(payload.email).trim().toLowerCase();
      if (email) {
        knownPresentEmailsRef.current.add(email);
        setEmailPresence('present');
      }
      const nextStats = { ...stats, migrated: stats.migrated + 1 };
      advance(nextStats);
    } catch (err) {
      setStats((prev) => ({ ...prev, failed: prev.failed + 1 }));
      notifications.show({
        color: 'red',
        message: (err as Error).message || 'Migrate from JSON failed',
      });
    } finally {
      setMigrating(false);
    }
  };

  const busy = verifying || migrating;
  const progressLabel =
    queue.length > 0 ? `File ${index + 1} of ${queue.length}` : null;

  const presenceBadge = (() => {
    if (emailPresence === 'loading') {
      return (
        <Badge color='gray' variant='light' leftSection={<Loader size={10} />}>
          Checking be2025…
        </Badge>
      );
    }
    if (emailPresence === 'present') {
      return (
        <Badge color='teal' variant='filled'>
          già in be2025
        </Badge>
      );
    }
    if (emailPresence === 'absent') {
      return (
        <Badge color='yellow' variant='light'>
          non presente
        </Badge>
      );
    }
    return (
      <Badge color='gray' variant='outline'>
        stato sconosciuto
      </Badge>
    );
  })();

  return (
    <>
      <Card withBorder padding='lg' radius='md'>
        <Stack gap='md'>
          <Group gap='sm'>
            <Upload size={22} color='var(--mantine-color-blue-6)' />
            <Title order={4}>Migrate from JSON</Title>
            <Badge color='blue' variant='light'>
              prod-safe · v0/v1 · queue
            </Badge>
          </Group>
          <Text size='sm' c='dimmed'>
            Drop one or many export JSON files (`kind:
            legacy_user_migration_export`, `legacy_source` v0 or v1). Old v0
            files still work. Files are confirmed one-by-one (Skip / Migrate).
            Migrate is fill-missing only.
          </Text>
          <div
            role='button'
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                inputRef.current?.click();
              }
            }}
            onClick={() => inputRef.current?.click()}
            onDragEnter={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={(event) => {
              event.preventDefault();
              setDragging(false);
            }}
            onDrop={onDrop}
            style={{
              border: `2px dashed ${
                dragging
                  ? 'var(--mantine-color-blue-5)'
                  : 'var(--mantine-color-gray-4)'
              }`,
              borderRadius: 12,
              padding: '28px 20px',
              textAlign: 'center',
              cursor: 'pointer',
              background: dragging
                ? 'var(--mantine-color-blue-0)'
                : 'var(--mantine-color-gray-0)',
              transition: 'background 120ms ease, border-color 120ms ease',
            }}
          >
            <Stack gap={6} align='center'>
              <Upload size={28} color='var(--mantine-color-blue-6)' />
              <Text fw={600} size='sm'>
                Drop JSON files here
              </Text>
              <Text size='xs' c='dimmed'>
                or click to choose · multi-file · sequential confirm
              </Text>
            </Stack>
          </div>
          <input
            ref={inputRef}
            type='file'
            accept='application/json,.json'
            multiple
            hidden
            onChange={(event) => {
              const files = event.currentTarget.files;
              event.currentTarget.value = '';
              void acceptFiles(files);
            }}
          />
        </Stack>
      </Card>

      <Modal
        opened={modalOpen}
        onClose={reset}
        title={
          progressLabel
            ? `Migration JSON · ${progressLabel}`
            : 'Migration JSON'
        }
        centered
        size='xl'
        closeOnClickOutside={!busy}
        closeOnEscape={!busy}
      >
        <Stack gap='md'>
          <Alert color='blue' title={filename ?? 'export.json'}>
            <Stack gap={8}>
              <Group gap='sm' wrap='wrap'>
                <Text size='sm' fw={600}>
                  {payload?.email ?? '—'}
                </Text>
                {presenceBadge}
              </Group>
              <Text size='sm'>
                {legacySource ? legacySource.toUpperCase() : '—'} · Legacy #
                {payload?.legacy_user_id ?? '—'}
              </Text>
              {payload?.exported_at ? (
                <Text size='xs' c='dimmed'>
                  Exported at {payload.exported_at}
                </Text>
              ) : null}
              {queue.length > 1 ? (
                <Text size='xs' c='dimmed'>
                  Queue · migrated {stats.migrated} · skipped {stats.skipped} ·
                  failed {stats.failed}
                </Text>
              ) : null}
            </Stack>
          </Alert>
          <Group justify='space-between' gap='sm' wrap='wrap'>
            <Button variant='subtle' color='gray' disabled={busy} onClick={reset}>
              Annulla coda
            </Button>
            <Group gap='sm'>
              <Button variant='default' disabled={busy} onClick={handleSkip}>
                Skip
              </Button>
              <Button
                color='violet'
                variant='light'
                loading={verifying}
                disabled={(busy && !verifying) || legacySource === 'v0'}
                onClick={() => void handleVerify()}
              >
                Verify{legacySource === 'v0' ? ' (v1 only)' : ''}
              </Button>
              <Button
                color='teal'
                loading={migrating}
                disabled={busy && !migrating}
                onClick={() => void handleMigrate()}
              >
                Migra
              </Button>
            </Group>
          </Group>
          {verification ? (
            <LegacyMigrationVerificationResult verification={verification} />
          ) : null}
        </Stack>
      </Modal>
    </>
  );
}

export default function AdminLegacyMigrationHub() {
  return (
    <Container size='lg' py='lg' px='md'>
      <Stack gap='xl'>
        <PageHeader
          title='Legacy Migration'
          subtitle='Choose the source platform to migrate into be2025. New users get legacy_source = v1-new; existing be2025 users get v1-partial (fill-only).'
          breadcrumbs={[
            { label: 'Admin', href: '/admin/users' },
            { label: 'Legacy Migration' },
          ]}
          showBackButton={false}
          actions={
            <Badge color='yellow' variant='filled'>
              non-prod only
            </Badge>
          }
        />

        <MigrateFromJsonPanel />

        <MigrationSection
          source='v1'
          title='Become v1 (vapor)'
          subtitle='Current slice: courses, videos, answers, feedback, progress → UserCourses / UserProducts.'
          badgeColor='orange'
          cards={V1_CARDS}
        />

        <MigrationSection
          source='v0'
          title='Become v0'
          subtitle='WordPress dump (SQLite): profilo, abbonamenti, oculus → Users / Devices / UserProducts. Separate from v1 remaps.'
          badgeColor='grape'
          cards={V0_CARDS}
        />
      </Stack>
    </Container>
  );
}
